import { useQueryClient } from "@tanstack/react-query";
import { useMutation } from "@tanstack/react-query";
import type { UseMutationResult } from "@tanstack/react-query";

import { updateTodo } from "@/tasks/services";
import { queryKeysTasks } from "@/tasks/config";

import type { CreateTodoParams, CreateTodoResponse, Todo } from "@/tasks/types";

interface UpdateTodoParams extends Partial<CreateTodoParams> {
  id: number;
}

interface UpdateTodoContext {
  previousTodo?: Todo;
  id: number;
}

const useUpdateTodo = (): UseMutationResult<
  CreateTodoResponse,
  Error,
  UpdateTodoParams
> => {
  const queryClient = useQueryClient();

  return useMutation<CreateTodoResponse, Error, UpdateTodoParams>({
    mutationFn: ({ id, ...params }) => updateTodo(id, params),
    onMutate: async ({ id, ...newData }) => {
      await queryClient.cancelQueries({
        queryKey: queryKeysTasks.detail(id),
      });

      const previousTodo = queryClient.getQueryData<Todo>(
        queryKeysTasks.detail(id),
      );

      queryClient.setQueryData<Todo>(queryKeysTasks.detail(id), (old) => {
        if (!old) return old;
        return { ...old, ...newData };
      });

      queryClient.setQueriesData<Todo[]>(
        { queryKey: queryKeysTasks.lists() },
        (old) => {
          if (!old) return old;
          return old.map((todo) =>
            todo.id === id ? { ...todo, ...newData } : todo,
          );
        },
      );

      return { previousTodo, id };
    },

    onError: (error, { id }, context) => {
      const typedContext = context as UpdateTodoContext | undefined;
      if (typedContext?.previousTodo) {
        queryClient.setQueryData(
          queryKeysTasks.detail(id),
          typedContext.previousTodo,
        );
      }

      queryClient.invalidateQueries({ queryKey: queryKeysTasks.lists() });

      console.error("Erro ao atualizar todo:", error);
    },

    onSettled: (data, error, { id }) => {
      queryClient.invalidateQueries({ queryKey: queryKeysTasks.detail(id) });
      queryClient.invalidateQueries({ queryKey: queryKeysTasks.lists() });
    },
  });
};

export { useUpdateTodo };
